import React, { useMemo } from 'react';
import './OrderProduct.css';
import ColorPreviewCanvas from '../ColorPreviewImage/ColorPreviewCanvas.js';
import { getImageUrl } from '../../utils/imageProxyUtils';

const getDiscountedPrice = (basePrice, discount = 0) => {
    const price = Number(basePrice) || 0;
    return price - (price * (Number(discount) || 0)) / 100;
};

const getSelectedSizeEntries = (selectedSizes) => {
    if (!selectedSizes || typeof selectedSizes !== 'object') return [];
    return Object.entries(selectedSizes).filter(([, qty]) => (Number(qty) || 0) > 0);
};

const getProductSizeNames = (productData, isFinished) => {
    if (!productData) return [];

    if (isFinished && productData.sizesOnly) {
        return Object.keys(productData.sizesOnly);
    }

    const firstColor = productData.colorsAndSizes
        ? Object.keys(productData.colorsAndSizes)[0]
        : null;
    const sizesObj = firstColor ? productData.colorsAndSizes[firstColor] : null;

    return sizesObj && typeof sizesObj === 'object' ? Object.keys(sizesObj) : [];
};

const getImageForSide = (images = [], side = 'front') => {
    if (!Array.isArray(images) || !images.length) return null;
    return images.find((img) => img.type === side) || images[0];
};

const getDesignImageUrl = (designData, side = 'front') => {
    if (!designData?.imgs?.length) return null;

    const byType = designData.imgs.find((img) => img.type === side);
    if (byType) return getImageUrl(byType);

    const legacyImage = designData.imgs[0];
    if (legacyImage?.[side]) return getImageUrl(legacyImage[side]);

    return getImageUrl(legacyImage);
};

const OrderProduct = ({
    id,
    color,
    quantity,
    side = 'front',
    size,
    itemType = 'design',
    designData,
    productData,
    pack = [],
    price,
    selectedSizes = null
}) => {
    const isFinished = itemType === 'finished';
    const itemQuantity = Number(quantity) || 1;
    const packArray = useMemo(() => (Array.isArray(pack) ? pack : []), [pack]);
    const selectedSizeEntries = useMemo(
        () => getSelectedSizeEntries(selectedSizes),
        [selectedSizes]
    );
    const hasSelectedSizes = selectedSizeEntries.length > 0;
    const productSizeNames = useMemo(
        () => getProductSizeNames(productData, isFinished),
        [productData, isFinished]
    );

    const productImageUrl = useMemo(() => {
        if (!productData?.imgs?.length) return null;

        const image = isFinished
            ? productData.imgs.find((img) => img.type === 'front') || productData.imgs[0]
            : getImageForSide(productData.imgs, side);

        return image ? getImageUrl(image) : null;
    }, [productData, side, isFinished]);

    const designImageUrl = useMemo(
        () => (isFinished ? null : getDesignImageUrl(designData, side)),
        [designData, side, isFinished]
    );

    const originalPrice = useMemo(() => {
        if (isFinished) {
            return Number(productData?.price || productData?.purchase) || 0;
        }

        return Number(designData?.price) || 0;
    }, [isFinished, productData, designData]);

    const discountPercentage = useMemo(() => {
        if (isFinished) return Number(productData?.discount) || 0;
        return Number(designData?.discount) || 0;
    }, [isFinished, productData, designData]);

    const finalPrice = useMemo(() => {
        if (price !== undefined && price !== null) return Number(price) || 0;
        return getDiscountedPrice(originalPrice, discountPercentage);
    }, [price, originalPrice, discountPercentage]);

    const displayedSizes = useMemo(() => {
        if (hasSelectedSizes) {
            return selectedSizeEntries.map(([sizeName, qty]) => `${qty}x ${sizeName}`);
        }

        if (size) return [size];

        if (packArray.length) {
            return packArray
                .map((qty, index) => {
                    const normalizedQty = Number(qty) || 0;
                    if (!normalizedQty) return null;
                    return `${normalizedQty}x ${productSizeNames[index] || `Taille ${index + 1}`}`;
                })
                .filter(Boolean);
        }

        return productSizeNames;
    }, [hasSelectedSizes, selectedSizeEntries, size, packArray, productSizeNames]);

    const piecesPerUnit = useMemo(() => {
        if (hasSelectedSizes) {
            return selectedSizeEntries.reduce((sum, [, qty]) => sum + (Number(qty) || 0), 0);
        }

        if (size) return 1;

        if (packArray.length) {
            return packArray.reduce((sum, qty) => sum + (Number(qty) || 0), 0);
        }

        return 1;
    }, [hasSelectedSizes, selectedSizeEntries, size, packArray]);

    const itemTotal = useMemo(() => {
        return finalPrice * piecesPerUnit * itemQuantity;
    }, [finalPrice, piecesPerUnit, itemQuantity]);

    const displayTitle = isFinished
        ? productData?.title || id
        : designData?.title || productData?.title || id;
    const displayCategory = productData?.category || 'Categorie';

    return (
        <div className="order-product">
            <div className="order-product-item">
                <div className="order-product-left">
                    <div className="order-product-container">
                        <div className="order-product-card-container">
                            <div className="order-image-container">
                                {productImageUrl ? (
                                    isFinished ? (
                                        <img
                                            src={productImageUrl}
                                            alt={displayTitle}
                                            className="order-product-image"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <ColorPreviewCanvas
                                            imageUrl={productImageUrl}
                                            color={color}
                                            className="order-product-canvas"
                                        />
                                    )
                                ) : (
                                    <div className="order-product-image-fallback">
                                        Image indisponible
                                    </div>
                                )}

                                {designImageUrl && (
                                    <img
                                        src={designImageUrl}
                                        alt="design"
                                        className="order-product-design-overlay"
                                        loading="lazy"
                                    />
                                )}
                            </div>
                            {discountPercentage > 0 && (
                                <div className="order-promo-badge">
                                    <span>-{discountPercentage}%</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="order-product-info">
                        <h3 className="order-product-title">{displayTitle}</h3>
                        <p className="order-product-category">{displayCategory}</p>
                        
                        <div className="order-product-attributes">
                            {color && (
                                <div className="order-attribute">
                                    <span className="attribute-label">Couleur:</span>
                                    <div className="order-color-wrapper">
                                        <div className="order-color-swatch" style={{ backgroundColor: color }}></div>
                                        <span className="attribute-value">{color}</span>
                                    </div>
                                </div>
                            )}

                            {!isFinished && (
                                <div className="order-attribute">
                                    <span className="attribute-label">Face:</span>
                                    <span className="attribute-value">{side === 'front' ? 'Face avant' : 'Face arriere'}</span>
                                </div>
                            )}
                        </div>
                        
                        {displayedSizes.length > 0 && (
                            <div className="order-sizes-section">
                                <span className="attribute-label">Tailles:</span>
                                <div className="order-sizes-list">
                                    {displayedSizes.slice(0, 5).map((sizeLabel, index) => (
                                        <span key={index} className="order-size-tag">
                                            {sizeLabel}
                                        </span>
                                    ))}
                                    {displayedSizes.length > 5 && (
                                        <span className="order-size-more">+{displayedSizes.length - 5}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <div className="order-pricing">
                            {discountPercentage > 0 && (
                                <span className="order-old-price">EUR {originalPrice.toFixed(2)}</span>
                            )}
                            <span className="order-current-price">EUR {finalPrice.toFixed(2)}</span>
                        </div>
                        
                        <div className="order-item-total">
                            <span>Total article:</span>
                            <strong>EUR {itemTotal.toFixed(2)}</strong>
                        </div>
                    </div>
                </div>
                
                <div className="order-product-right">
                    <div className="order-quantity-box">
                        <span className="quantity-label">Quantite</span>
                        <span className="quantity-value">{itemQuantity}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderProduct;
